import { useCallback, useMemo } from "react";
import { useQueryData } from "@src/hooks/useQueryData";
import type { SelectOptionData, SelectValue } from "@shared/ui/select";

type IdValue = string | number | boolean | undefined;

export type SelectChangeValue<M extends boolean, V> = M extends true ? V[] : V | null;

export type UseBusinessSelectParamsBase<
    TData extends object,
    Id extends IdValue,
    T extends object = TData,
    M extends boolean = false,
> = {
    queryKey: readonly unknown[];
    loadOptions: (ctx: { signal?: AbortSignal }) => Promise<TData[]>;
    getOptionData: (option: T) => SelectOptionData<Id, unknown>;
    augmentOptions?: (dataOptions: TData[]) => T[];
    transformOptions?: (items: TData[]) => TData[];
    disabled: boolean | undefined;
    value: SelectValue<M, T | Id> | null | undefined;
    multiple: M | undefined;
    onChange?: (value: M extends true ? T[] : T | null, rawValue: SelectChangeValue<M, Id>) => void;
    optionsLoadEnabled?: boolean;
    optionsStaleTime?: number;
};

export type UseBusinessSelectResultBase<TData, Id extends IdValue, T extends object, M extends boolean = false> = {
    /** Сохраняет связь с типом данных API (`TData`). */
    readonly dataRowType?: TData;
    options: T[] | null;
    getOptionData: (option: T) => SelectOptionData<Id, unknown>;
    /**
     * Любой fetch опций (`isFetching`). \
     * Для overlay UI: blocking = `isLoading && options == null`, soft refresh = `isLoading && options != null`.
     */
    isLoading: boolean;
    needsResolution: boolean;
    resolvedValue: SelectValue<M, Id> | null;
    isUnresolved: boolean;
    unresolvedValueId: Id | null;
    handleChange: (value: SelectChangeValue<M, Id>) => void;
};

function isEntity<T extends object>(item: unknown): item is T {
    return item !== null && typeof item === "object";
}

function resolveItem<T extends object, Id extends IdValue>(
    item: unknown,
    getOptionData: (option: T) => SelectOptionData<Id, unknown>,
): Id {
    if (isEntity<T>(item)) return getOptionData(item).value;
    if (isIdPrimitive(item)) {
        // TODO(types): remove assertion when Id is tied to IdValue without opaque instantiation
        return item as Id;
    }
    throw new Error("useBusinessSelect: unsupported value type for resolution");
}

function hasUnresolvedIds<T extends object, Id extends IdValue, M extends boolean>(
    value: SelectValue<M, T | Id> | null | undefined,
    multiple: M | undefined,
): boolean {
    if (value == null) return false;
    if (multiple === true) {
        return Array.isArray(value) && value.some((item) => !isEntity<T>(item));
    }
    return !isEntity<T>(value);
}

function isIdPrimitive(item: unknown): item is string | number | boolean {
    return typeof item === "string" || typeof item === "number" || typeof item === "boolean";
}

function loadedDataAsOptions<T extends object>(data: readonly object[]): T[] {
    const result: T[] = [];
    for (const item of data) {
        if (isEntity<T>(item)) {
            result.push(item);
        }
    }
    return result;
}

function extractValueEntities<T extends object, Id extends IdValue, M extends boolean>(
    value: SelectValue<M, T | Id> | null | undefined,
    multiple: M | undefined,
): T[] {
    if (value == null) return [];
    if (multiple === true) {
        const arr = Array.isArray(value) ? value : [];
        return arr.filter((item): item is T => isEntity<T>(item));
    }
    return isEntity<T>(value) ? [value] : [];
}

/**
 * Загрузка опций и resolve Id для {@link BusinessSelect}.
 */
export function useBusinessSelect<
    TData extends object,
    Id extends IdValue,
    T extends object = TData,
    M extends boolean = false,
>(params: UseBusinessSelectParamsBase<TData, Id, T, M>): UseBusinessSelectResultBase<TData, Id, T, M> {
    const {
        queryKey,
        loadOptions: queryFn,
        getOptionData,
        augmentOptions,
        transformOptions,
        disabled = false,
        value,
        multiple,
        onChange,
        optionsLoadEnabled = true,
        optionsStaleTime,
    } = params;

    const needsResolution = hasUnresolvedIds(value, multiple);
    const optionsQuery = useQueryData({
        queryKey: [...queryKey],
        queryFn: async ({ signal }) => {
            const data = await queryFn({ signal });
            if (!Array.isArray(data)) {
                throw new Error("useBusinessSelect: loadOptions must resolve to an array");
            }
            return data;
        },
        enabled: optionsLoadEnabled && (!disabled || needsResolution),
        ...(optionsStaleTime != null ? { staleTime: optionsStaleTime } : {}),
    });

    const hasResolvedOptionsData = optionsQuery.isSuccess || optionsQuery.data !== undefined;

    const dataOptions = useMemo(() => {
        if (hasResolvedOptionsData === false) return null;
        const raw = optionsQuery.data;
        if (raw == null) return null;
        return transformOptions != null ? transformOptions(raw) : raw;
    }, [hasResolvedOptionsData, optionsQuery.data, transformOptions]);

    const baseOptions = useMemo((): T[] | null => {
        if (dataOptions == null) return null;
        if (augmentOptions != null) return augmentOptions(dataOptions);
        return loadedDataAsOptions<T>(dataOptions);
    }, [dataOptions, augmentOptions]);

    const valueEntities = useMemo((): T[] => extractValueEntities<T, Id, M>(value, multiple), [value, multiple]);

    const options = useMemo((): T[] | null => {
        if (baseOptions !== null) {
            const idsInBase = new Set(baseOptions.map((o) => getOptionData(o).value));
            const missing = valueEntities.filter((e) => !idsInBase.has(getOptionData(e).value));
            return missing.length > 0 ? [...missing, ...baseOptions] : baseOptions;
        }
        return valueEntities.length > 0 ? valueEntities : null;
    }, [baseOptions, valueEntities, getOptionData]);

    const resolvedValue = useMemo((): SelectValue<M, Id> | null => {
        if (value == null) return (multiple === true ? [] : null) as SelectValue<M, Id> | null;
        if (multiple === true) {
            const arr = Array.isArray(value) ? value : [];
            return arr.map((item) => resolveItem(item, getOptionData)) as SelectValue<M, Id> | null;
        }
        if (value == null || Array.isArray(value)) return null;
        return resolveItem(value, getOptionData) as SelectValue<M, Id> | null;
    }, [value, multiple, getOptionData]);

    const isLoading = optionsQuery.isFetching;

    const isUnresolved = useMemo(() => {
        if (multiple === true) return false;
        if (!needsResolution || optionsQuery.isFetching || !hasResolvedOptionsData) return false;
        if (options == null) return false;
        const id = resolvedValue as Id | null;
        if (id == null) return false;
        return !options.some((o) => getOptionData(o).value === id);
    }, [multiple, needsResolution, optionsQuery.isFetching, hasResolvedOptionsData, options, resolvedValue, getOptionData]);

    const unresolvedValueId = useMemo((): Id | null => {
        if (multiple === true) return null;
        if (!needsResolution) return null;
        const id = resolvedValue as Id | null;
        if (id == null) return null;
        if (isLoading || isUnresolved) return id;
        return null;
    }, [multiple, needsResolution, resolvedValue, isLoading, isUnresolved]);

    const seedOptionsCache = useCallback(
        (entities: T[]) => {
            if (entities.length === 0) return;
            optionsQuery.setDataForKey<TData[]>([...queryKey], (old) => {
                if (old == null) return entities as unknown as TData[];
                const next = [...old];
                for (const entity of entities) {
                    const id = getOptionData(entity).value;
                    if (!next.some((o) => getOptionData(o as unknown as T).value === id)) {
                        next.push(entity as unknown as TData);
                    }
                }
                return next;
            });
        },
        [getOptionData, optionsQuery, queryKey],
    );

    const handleChange = useCallback(
        (changeValue: SelectChangeValue<M, Id>) => {
            if (onChange == null) return;
            const opts = options ?? [];
            const byId = new Map(opts.map((o) => [getOptionData(o).value, o]));
            if (multiple === true) {
                const arr = Array.isArray(changeValue) ? changeValue : [];
                const entities = arr.map((id) => byId.get(id)).filter((e): e is T => e != null);
                seedOptionsCache(entities);
                onChange(entities as M extends true ? T[] : T | null, arr as SelectChangeValue<M, Id>);
            } else {
                if (changeValue == null) {
                    onChange(null as M extends true ? T[] : T | null, changeValue as SelectChangeValue<M, Id>);
                    return;
                }
                const entity = byId.get(changeValue as Id) ?? null;
                if (entity != null) {
                    seedOptionsCache([entity]);
                }
                onChange(entity as M extends true ? T[] : T | null, changeValue as SelectChangeValue<M, Id>);
            }
        },
        [onChange, options, getOptionData, multiple, seedOptionsCache],
    );

    return {
        options,
        getOptionData,
        isLoading,
        needsResolution,
        resolvedValue,
        isUnresolved,
        unresolvedValueId,
        handleChange,
    };
}
